import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TimeApi, AdvanceTimeRequest } from '../api/timeApi';

const TIME_QUERY_KEY = ['dev', 'time'] as const;

interface UseGetCurrentTimeOptions {
  /** Enable auto-refresh (polls every 1 second) */
  autoRefresh?: boolean;
}

/**
 * Creates time manipulation hooks with the provided timeApi instance.
 * This factory pattern allows both admin portal and mobile app to use their
 * own configured API clients.
 *
 * @param timeApi - The time API client instance
 * @returns Object containing all time manipulation hooks
 */
export const createTimeManipulationHooks = (timeApi: TimeApi) => {
  /**
   * Get current server time with optional auto-refresh
   */
  const useGetCurrentTime = (options: UseGetCurrentTimeOptions = {}) => {
    const { autoRefresh = false } = options;

    return useQuery({
      queryKey: TIME_QUERY_KEY,
      queryFn: timeApi.getCurrentTime,
      // Auto-refresh every 1 second when enabled
      refetchInterval: autoRefresh ? 1000 : false,
      // Keep previous data while refreshing
      placeholderData: previousData => previousData,
    });
  };

  /**
   * Set time to specific point
   */
  const useSetTime = () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: (time: Date) => timeApi.setTime(time),
      onSuccess: () => {
        // Invalidate to trigger refresh
        queryClient.invalidateQueries({ queryKey: TIME_QUERY_KEY });
      },
    });
  };

  /**
   * Advance time by specified units
   */
  const useAdvanceTime = () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: (request: AdvanceTimeRequest) => timeApi.advanceTime(request),
      onSuccess: () => {
        // Invalidate to trigger refresh
        queryClient.invalidateQueries({ queryKey: TIME_QUERY_KEY });
      },
    });
  };

  /**
   * Reset to system time
   */
  const useResetTime = () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: timeApi.resetTime,
      onSuccess: () => {
        // Invalidate to trigger refresh
        queryClient.invalidateQueries({ queryKey: TIME_QUERY_KEY });
      },
    });
  };

  return {
    useGetCurrentTime,
    useSetTime,
    useAdvanceTime,
    useResetTime,
  };
};

/**
 * Type representing the time manipulation hooks
 */
export type TimeManipulationHooks = ReturnType<typeof createTimeManipulationHooks>;
